import { useEffect, useRef } from 'react'

export const TARGET_ELEMENT_ID = 'google_translate_element'

export const GOOGLE_BRAND_ROW_DATA_ATTRIBUTE = {
  attribute: 'data-translated-by-google',
  value: 'removed-when-popover-closed',
}

export const useGoogleTranslateWidget = () => {
  const targetElementRef = useRef<HTMLDivElement | null>(null)
  const styleChangedFlag = useRef(false)
  const isLoadedRef = useRef(false)

  const getElementsInDropdownTrigger = () => {
    const dropdownTrigger = targetElementRef.current?.querySelector<HTMLDivElement>('.goog-te-gadget-simple')
    const dropdownTriggerLeftIcon = dropdownTrigger?.querySelector<HTMLImageElement>('.goog-te-gadget-icon')
    const dropdownTriggerLabelWrapper = dropdownTrigger?.querySelector<HTMLAnchorElement>('span > a')
    const dropdownTriggerLabel = dropdownTrigger?.querySelector<HTMLSpanElement>('span > a > span')
    const dropdownTriggerBlankSpaceImages = dropdownTrigger?.querySelectorAll<HTMLImageElement>('span > a > img')
    const dropdownTriggerPipeDividingCharacter = dropdownTrigger?.querySelector<HTMLSpanElement>(
      'span > a > span:nth-of-type(2)'
    )
    const dropdownTriggerChevronIcon = dropdownTrigger?.querySelector<HTMLSpanElement>('span > a > span:last-of-type')

    return {
      dropdownTrigger,
      dropdownTriggerLeftIcon,
      dropdownTriggerLabelWrapper,
      dropdownTriggerLabel,
      dropdownTriggerBlankSpaceImages,
      dropdownTriggerPipeDividingCharacter,
      dropdownTriggerChevronIcon,
    }
  }

  const getElementsInDropdownList = (iframe: HTMLIFrameElement) => {
    const documentElement = iframe.contentDocument?.documentElement
    const body = documentElement?.querySelector<HTMLBodyElement>('body')
    const listWrapper = body?.querySelector<HTMLDivElement>('body > div')
    const table = listWrapper?.querySelector<HTMLTableElement>('table')
    const tableRowsWrapper = table?.querySelector<HTMLTableCellElement>('tbody > tr > td')

    const tableRows = table?.querySelectorAll<HTMLDivElement>('tbody > tr > td > a > div')
    const googleBrandRows = tableRowsWrapper?.querySelectorAll(
      `[${GOOGLE_BRAND_ROW_DATA_ATTRIBUTE.attribute}=${GOOGLE_BRAND_ROW_DATA_ATTRIBUTE.value}]`
    )
    return {
      documentElement,
      body,
      listWrapper,
      table,
      tableRowsWrapper,
      tableRows,
      googleBrandRows,
    }
  }

  /**
   * Observe DOM changes and customise Google Translate widget's styles
   */
  const observeTranslateIframe = () => {
    const observer = new MutationObserver((mutations) => {
      const iframe = document.querySelector<HTMLIFrameElement>('body > iframe.skiptranslate')

      if (iframe) {
        // When iframe found and loaded, execute the callback
        if (!iframe.onload) {
          iframe.onload = () => customizeDropdownTriggerStyles()
        }

        // Change styles of language list popover
        mutations.forEach((mutation) => {
          if (
            mutation.type === 'attributes' &&
            mutation.attributeName === 'style' &&
            mutation.target.nodeName === 'IFRAME'
          ) {
            const popoverIframe = mutation.target as HTMLIFrameElement
            !popoverIframe.style.display ? customizeDropdownListStyles(popoverIframe) : reset(iframe)
          }
        })
      }
    })

    observer.observe(document.body, {
      subtree: true,
      attributes: true,
    })
  }

  const reset = (iframe: HTMLIFrameElement) => {
    // Reset flag
    styleChangedFlag.current = false
    // Remove additionally attached elements
    const { googleBrandRows } = getElementsInDropdownList(iframe)

    if (googleBrandRows) {
      googleBrandRows.forEach((googleBrandRow) => {
        googleBrandRow.remove()
      })
    }
  }

  const customizeDropdownListStyles = (iframe: HTMLIFrameElement) => {
    if (styleChangedFlag.current) {
      return
    }

    // Change iframe style
    const { top } = getComputedStyle(iframe)
    const topOffset = 4
    const newTopValue = +top.replace('px', '') + topOffset
    iframe.style.top = `${newTopValue}px`
    iframe.style.boxShadow = 'none'
    iframe.style.width = '160px'
    iframe.style.boxShadow = '0px 2px 4px -2px rgba(0, 0, 0, 0.10), 0px 4px 6px -1px rgba(0, 0, 0, 0.10)'
    iframe.style.border = '1px solid #E4E4E7'
    iframe.style.borderRadius = '6px'
    iframe.style.backgroundColor = 'white'

    // Change popover list style
    const { documentElement, listWrapper, table, tableRows, tableRowsWrapper } = getElementsInDropdownList(iframe)

    if (listWrapper) {
      listWrapper.style.boxSizing = 'border-box'
      listWrapper.style.width = '100%'
      listWrapper.style.height = 'fit-content'
      listWrapper.style.backgroundColor = 'white'
      listWrapper.style.border = 'none'
    }

    if (table) {
      table.style.width = '100%'
    }

    if (tableRows) {
      tableRows.forEach((row) => {
        row.style.boxSizing = 'border-box'
        row.style.display = 'flex'
        row.style.alignItems = 'center'
        row.style.height = '40px'
        row.style.padding = '6px 8px'
        row.style.borderRadius = '4px'
        row.style.fontSize = '14px'
        row.style.fontWeight = '400'
        row.style.lineHeight = '20px'
        row.style.color = '#18181b'
        row.onmouseenter = () => {
          row.style.backgroundColor = '#f4f4f5'
        }
        row.onmouseleave = () => {
          row.style.backgroundColor = 'unset'
        }

        const indicator = row.querySelector<HTMLSpanElement>('.indicator')
        if (indicator) {
          indicator.innerText = ''
          indicator.style.width = '16px'
          indicator.style.height = '16px'
          indicator.style.marginRight = '8px'
        }

        const targetElement = targetElementRef.current
        if (targetElement) {
          const { dropdownTriggerLabel } = getElementsInDropdownTrigger()
          const dropdownTriggerText = dropdownTriggerLabel?.textContent
          const languageOptionText = row.querySelector<HTMLSpanElement>('.text')

          // Change indicator style to svg icon
          if (dropdownTriggerText === languageOptionText?.textContent && indicator) {
            indicator.style.backgroundImage = 'url(/images/google-translate-widget-check-mark.svg)'
            indicator.style.backgroundSize = 'contain'
          }
        }
      })
    }

    // Add an additional row to indicate "Translated by Google"
    const googleBrandRow = document.createElement('div')
    const icon = document.createElement('span')
    googleBrandRow.setAttribute(GOOGLE_BRAND_ROW_DATA_ATTRIBUTE.attribute, GOOGLE_BRAND_ROW_DATA_ATTRIBUTE.value)
    googleBrandRow.textContent = 'Translated by Google'
    googleBrandRow.style.display = 'flex'
    googleBrandRow.style.alignItems = 'center'
    googleBrandRow.style.boxSizing = 'border-box'
    googleBrandRow.style.height = '40px'
    googleBrandRow.style.padding = '6px 8px'
    googleBrandRow.style.fontSize = '10px'
    googleBrandRow.style.fontStyle = 'italic'
    googleBrandRow.style.fontWeight = '400'
    googleBrandRow.style.lineHeight = '24px'
    googleBrandRow.style.color = '#71717A'
    icon.style.width = '16px'
    icon.style.height = '16px'
    icon.style.backgroundImage = 'url(/images/google-translate-widget-grayscale-icon.svg)'
    icon.style.backgroundSize = 'contain'
    icon.style.marginRight = '8px'
    googleBrandRow.prepend(icon)

    if (tableRowsWrapper) {
      tableRowsWrapper.append(googleBrandRow)
    }

    // Adjust iframe's height to fit the inner content
    iframe.style.height = documentElement?.clientHeight ? `${documentElement.clientHeight}px` : 'auto'

    // Change the value of the flag to true to prevent observer callback to be executed endlessly
    styleChangedFlag.current = true
  }

  const customizeDropdownTriggerStyles = () => {
    const targetElement = targetElementRef.current

    setTimeout(() => {
      if (targetElement) {
        // Change dropdown trigger element's styles
        const {
          dropdownTrigger,
          dropdownTriggerLeftIcon,
          dropdownTriggerLabelWrapper,
          dropdownTriggerLabel,
          dropdownTriggerBlankSpaceImages,
          dropdownTriggerPipeDividingCharacter,
          dropdownTriggerChevronIcon,
        } = getElementsInDropdownTrigger()

        if (dropdownTrigger) {
          dropdownTrigger.style.padding = '8px 16px'
          dropdownTrigger.style.border = '1px solid #e4e4e7'
          dropdownTrigger.style.borderRadius = '6px'
        }

        if (dropdownTriggerLeftIcon) {
          dropdownTriggerLeftIcon.style.display = 'none'
        }

        if (dropdownTriggerLabelWrapper) {
          dropdownTriggerLabelWrapper.style.display = 'inline-flex'
          dropdownTriggerLabelWrapper.style.alignItems = 'center'
          dropdownTriggerLabelWrapper.style.margin = '0'
        }

        if (dropdownTriggerLabel) {
          dropdownTriggerLabel.style.fontSize = '14px'
          dropdownTriggerLabel.style.fontWeight = '500'
          dropdownTriggerLabel.style.lineHeight = '20px'
          dropdownTriggerLabel.style.color = '#09090B'
        }

        if (dropdownTriggerBlankSpaceImages) {
          dropdownTriggerBlankSpaceImages.forEach((blankSpaceImage) => (blankSpaceImage.style.display = 'none'))
        }

        if (dropdownTriggerPipeDividingCharacter) {
          dropdownTriggerPipeDividingCharacter.style.display = 'none'
        }

        if (dropdownTriggerChevronIcon) {
          dropdownTriggerChevronIcon.innerText = ''
          dropdownTriggerChevronIcon.style.display = 'inline-block'
          dropdownTriggerChevronIcon.style.width = '16px'
          dropdownTriggerChevronIcon.style.height = '16px'
          dropdownTriggerChevronIcon.style.backgroundImage = 'url(/images/google-translate-widget-chevron-down.svg)'
          dropdownTriggerChevronIcon.style.backgroundSize = 'contain'
          dropdownTriggerChevronIcon.style.marginLeft = '8px'
        }
      }

      // show widget after changing all the styles
      if (targetElement) {
        targetElement.style.display = 'block'
      }
    }, 500)
  }

  const loadGoogleTranslateScript = () => {
    if (isLoadedRef.current) {
      return
    }

    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.async = true
    script.src = '//translate.google.com/translate_a/element.js?hl=en&cb=googleTranslateElementInit' // hl = host language(the language of widget interface)
    document.body.appendChild(script)
  }

  useEffect(() => {
    loadGoogleTranslateScript()

    window.googleTranslateElementInit = () => {
      if (window.google && targetElementRef.current) {
        new window.google.translate.TranslateElement(
          {
            pageLanguage: 'en',
            includedLanguages: 'es,fr,ru,ka',
            layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
            autoDisplay: true,
            gaTrack: false,
          },
          targetElementRef.current
        )
      }
    }

    observeTranslateIframe()

    isLoadedRef.current = true

    return () => {
      if (window.googleTranslateElementInit) {
        delete window.googleTranslateElementInit
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { targetElementRef }
}
